'use client'

import Image from 'next/image'
import Link from 'next/link'
import { createPortal } from 'react-dom'
import { useState, useMemo, useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { logger } from '@/utils/logger'
import { validateEmailDomain } from '@/utils/emailValidation'
import type { CartItem } from '@/types/marketplace'
import { trackQuoteSubmitted } from '@/utils/marketplaceQuoteAnalytics'
import SelectableCard from '@/components/common/SelectableCard'
import { cartItemCompositeKey, useQuoteCart } from '@/components/marketplace/QuoteCart'
import { CART_ICON_PATH } from '@/components/marketplace/quoteCartDock'
import { formatPrice } from '@/utils/microsoftFormatters'
import { CartLineLogoTile } from '@/components/marketplace/MicrosoftLogo'
import RollingNumber from '@/components/marketplace/RollingNumber'
import QuantityStepper from '@/components/marketplace/QuantityStepper'
import RemoveLineButton from '@/components/marketplace/RemoveLineButton'
import { API_ENDPOINTS } from '@/constants'
import { SEGMENT_CONFIG_MAP } from '@/constants/microsoftCategories'
import {
  buildMarketplaceQuoteCartHubSpotText,
  cartItemSkuDisplay,
  HUBSPOT_MARKETPLACE_QUOTE_CART_FIELD,
  initSourcebuster,
  submitHubSpotForm,
} from '@/utils/marketplaceFormHelpers'

const HUBSPOT_PORTAL_ID = '9363439'
const HUBSPOT_FORM_ID = 'cab10eda-19ce-4ab9-9996-11a8ecc85eb1'
// Concierge router link; query params keyed by the router's form field
// references (PersonFirstName etc.) prefill its booking form
const CHILI_PIPER_SCHEDULING_URL = 'https://secureframe.chilipiper.com/concierge-router/link/marketplace---form-fill'
const CHILI_PIPER_ORIGIN = new URL(CHILI_PIPER_SCHEDULING_URL).origin

interface QuoteFormData {
  firstname: string
  lastname: string
  email: string
  company: string
  phone?: string
  company_domain?: string
  microsoft_tenant_id?: string
  cmmc_level?: string
  message?: string
}

interface QuotePageContentProps {
  /** Cart line resolved from ?product=&segment= — added to the cart on mount (MAR-993) */
  prefillItem?: CartItem | null
}

export default function QuotePageContent({ prefillItem = null }: QuotePageContentProps) {
  const cart = useQuoteCart()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [quoteSuccess, setQuoteSuccess] = useState(false)
  const [callScheduled, setCallScheduled] = useState(false)
  const [scheduleCallChoice, setScheduleCallChoice] = useState<boolean | null>(null)
  const [schedulerLead, setSchedulerLead] = useState<{
    firstname: string
    lastname: string
    email: string
    company: string
  } | null>(null)
  const [schedulerOpen, setSchedulerOpen] = useState(false)
  const [callBooked, setCallBooked] = useState(false)
  const prefillHandled = useRef(false)
  const schedulerCloseRef = useRef<HTMLButtonElement | null>(null)
  const schedulerReopenRef = useRef<HTMLButtonElement | null>(null)
  const schedulerIframeRef = useRef<HTMLIFrameElement | null>(null)
  const schedulerDialogRef = useRef<HTMLDivElement | null>(null)
  const schedulerWasOpen = useRef(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<QuoteFormData>()

  const totals = useMemo(() => {
    let totalMonthly = 0
    let totalAnnual = 0
    let totalLicenses = 0

    for (const item of cart.items) {
      totalMonthly += item.erpMonthly * item.quantity
      totalAnnual += item.erpAnnual * item.quantity
      totalLicenses += item.quantity
    }

    return { totalMonthly, totalAnnual, totalLicenses }
  }, [cart.items])

  const hasGovSegment = cart.items.some((item) => ['gcc', 'gcc-high', 'secret', 'top-secret'].includes(item.segment))
  const cartSegments = cart.items.map((item) => item.segment)
  const wantsCall = scheduleCallChoice ?? true

  // Initialize sourcebuster for UTM tracking on mount
  useEffect(() => {
    initSourcebuster()
  }, [])

  // Prefill the cart from ?product=&segment= once per mount (MAR-993). Skipped
  // when the same product+segment line is already in the cart (e.g. the CTA
  // click was preceded by an "Add to Quote" click) so quantities don't double.
  useEffect(() => {
    if (!cart.isLoaded || !prefillItem || prefillHandled.current) return
    prefillHandled.current = true
    const key = cartItemCompositeKey(prefillItem)
    if (!cart.items.some((item) => cartItemCompositeKey(item) === key)) {
      cart.addItem(prefillItem)
    }
  }, [cart, prefillItem])

  // The embedded booking app announces progress to the parent window. Flip the
  // success copy once a slot is booked, and close the modal when its own UI says to.
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== CHILI_PIPER_ORIGIN) return
      // Origin alone would trust any window on that origin; only our iframe counts
      if (!schedulerIframeRef.current || e.source !== schedulerIframeRef.current.contentWindow) return
      const data = e.data as { type?: string; action?: string } | null
      if (data?.type !== 'CHILI_SCHEDULING_EVENT') return
      if (data.action === 'booked' || data.action === 'booking-confirmed') setCallBooked(true)
      if (data.action === 'close') setSchedulerOpen(false)
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [])

  useEffect(() => {
    if (!schedulerOpen) {
      // Return focus to the reopen button after a close; no-op when it is
      // absent, e.g. after booking
      if (schedulerWasOpen.current) {
        schedulerWasOpen.current = false
        schedulerReopenRef.current?.focus()
      }
      return
    }
    schedulerWasOpen.current = true
    schedulerCloseRef.current?.focus()
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSchedulerOpen(false)
      if (e.key !== 'Tab') return
      // Trap Tab inside the dialog: the page behind the overlay is visually
      // obscured but would otherwise still be reachable by keyboard
      const focusables = schedulerDialogRef.current?.querySelectorAll<HTMLElement>('button, a[href], iframe')
      if (!focusables || focusables.length === 0) return
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previousOverflow
    }
  }, [schedulerOpen])

  useEffect(() => {
    if (!quoteSuccess) return
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch {
      // Ignore non-browser test/runtime environments without scroll support.
    }
  }, [quoteSuccess])

  const onSubmit = async (data: QuoteFormData) => {
    setIsSubmitting(true)
    setSubmitError(null)

    const firstname = data.firstname.trim()
    const lastname = data.lastname.trim()

    const quoteCartText =
      buildMarketplaceQuoteCartHubSpotText(cart.items, {
        monthly: totals.totalMonthly,
        annual: totals.totalAnnual,
      }) + (wantsCall ? '\n\nFollow-up: solutions expert meeting requested (scheduler shown on submit)' : '')

    try {
      // Submit to HubSpot via shared helper (auto-enriches with UTM fields)
      const hubspotResult = await submitHubSpotForm(
        HUBSPOT_PORTAL_ID,
        HUBSPOT_FORM_ID,
        {
          firstname,
          lastname,
          email: data.email,
          company: data.company,
          phone: data.phone ?? '',
          company_domain: data.company_domain ?? '',
          microsoft_tenant_id: data.microsoft_tenant_id ?? '',
          cmmc_level: hasGovSegment ? (data.cmmc_level ?? '') : '',
          message: data.message ?? '',
          [HUBSPOT_MARKETPLACE_QUOTE_CART_FIELD]: quoteCartText,
        },
        { pageName: 'Marketplace Quote Request' },
      )

      // Zapier backup — it carries the same cart text, so it alone is enough to
      // capture the lead
      const zapierOk = await fetch(API_ENDPOINTS.MARKETPLACE_QUOTE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contact: { name: `${firstname} ${lastname}`, email: data.email, company: data.company, phone: data.phone },
          vendor: 'microsoft',
          cart: cart.items,
          totals: { monthly: totals.totalMonthly, annual: totals.totalAnnual },
          quoteData: quoteCartText,
        }),
      })
        .then((res) => res.ok)
        .catch(() => false)

      if (!hubspotResult.success && !zapierOk) {
        logger.error('Quote submission failed on both channels', { hubspotStatus: hubspotResult.status })
        setSubmitError('Something went wrong. Please try again or contact us directly.')
        return
      }

      if (wantsCall) {
        setSchedulerLead({ firstname, lastname, email: data.email, company: data.company })
        setSchedulerOpen(true)
      }

      trackQuoteSubmitted({
        surface: 'quote_page',
        products: cart.items.map((item) => item.productSlug),
        segments: cartSegments,
        item_count: cart.items.length,
        license_count: totals.totalLicenses,
        monthly_total: Math.round(totals.totalMonthly * 100) / 100,
        annual_total: Math.round(totals.totalAnnual * 100) / 100,
        schedule_call: wantsCall,
        hubspot_success: hubspotResult.success,
      })

      cart.clearCart()
      setCallScheduled(wantsCall)
      setQuoteSuccess(true)
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error))
      logger.error('Quote submission failed', { message: err.message })
      setSubmitError('Something went wrong. Please try again or contact us directly.')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!cart.isLoaded) return null

  // Success state
  if (quoteSuccess) {
    const schedulingUrl = schedulerLead
      ? `${CHILI_PIPER_SCHEDULING_URL}?${new URLSearchParams({
          PersonFirstName: schedulerLead.firstname,
          PersonLastName: schedulerLead.lastname,
          PersonEmail: schedulerLead.email,
          CompanyName: schedulerLead.company,
          id: schedulerLead.email,
        }).toString()}`
      : CHILI_PIPER_SCHEDULING_URL
    return (
      <section className="py-16 lg:py-24">
        <div className="container max-w-2xl text-center">
          {/* The same success illustration the product's checkout ends on */}
          <Image
            src="/images/quote-requested.png"
            alt=""
            width={481}
            height={420}
            priority
            className="mx-auto mb-6 h-auto w-40 sm:w-48"
          />
          <h1 className="mb-3 text-2xl tracking-normal">Quote Requested</h1>
          <p className="mb-6 text-sm text-primary-ink-85">
            {callBooked
              ? "Your meeting with a solutions expert is booked. We'll review your selections and send a quote within 1 business day."
              : callScheduled
                ? "We'll review your selections and send a quote within 1 business day. Pick a time below to meet with a solutions expert."
                : "We'll review your selections and send a quote within 1 business day."}
          </p>
          {callScheduled && !callBooked && !schedulerOpen && (
            <button
              ref={schedulerReopenRef}
              type="button"
              onClick={() => setSchedulerOpen(true)}
              className="mb-6 inline-flex items-center justify-center rounded-md bg-primary-ink-100 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-ink-85 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-green-85">
              Pick a time for your meeting
            </button>
          )}
          <div>
            <Link href="/marketplace/microsoft" className="text-sm text-[#0A7849] hover:underline">
              ← Continue shopping
            </Link>
          </div>
        </div>

        {schedulerOpen &&
          createPortal(
            <div
              ref={schedulerDialogRef}
              role="dialog"
              aria-modal="true"
              aria-label="Schedule your meeting"
              onClick={() => setSchedulerOpen(false)}
              className="fixed inset-0 z-[99999] flex items-center justify-center bg-primary-ink-100/60 p-4">
              <div
                onClick={(e) => e.stopPropagation()}
                className="flex h-[85vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-light">
                <div className="flex items-center justify-between border-b border-primary-ink-6 px-4 py-3">
                  <h2 className="text-base font-medium tracking-normal text-primary-ink-100">Schedule your meeting</h2>
                  <button
                    ref={schedulerCloseRef}
                    type="button"
                    onClick={() => setSchedulerOpen(false)}
                    aria-label="Close scheduler"
                    className="flex h-8 w-8 items-center justify-center rounded text-primary-ink-60 transition-colors hover:bg-secondary-gray-100 hover:text-primary-ink-85 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-green-85">
                    <svg
                      aria-hidden="true"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <iframe
                  ref={schedulerIframeRef}
                  src={schedulingUrl}
                  title="Scheduling calendar"
                  className="w-full flex-1 border-0"
                />
                <div className="border-t border-primary-ink-6 px-4 py-2 text-right">
                  <a
                    href={schedulingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-[#0A7849] hover:underline">
                    Open in a new tab
                  </a>
                </div>
              </div>
            </div>,
            document.body,
          )}
      </section>
    )
  }

  // Empty cart
  if (cart.items.length === 0) {
    return (
      <section className="min-w-0 py-16 lg:py-24">
        <div className="container max-w-lg">
          <div className="px-6 py-12 text-center sm:px-10">
            <div
              className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-primary-ink-6"
              aria-hidden="true">
              <svg
                className="h-7 w-7 text-primary-ink-100"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d={CART_ICON_PATH} />
              </svg>
            </div>
            <h1 className="mb-2 text-2xl font-medium tracking-normal text-primary-ink-100">Your quote cart is empty</h1>
            <p className="mx-auto mb-8 max-w-xl text-sm leading-relaxed text-primary-ink-60">
              Browse the Microsoft Marketplace and add licenses to your cart. When you&apos;re ready, return here to
              request a quote.
            </p>
            <Link
              href="/marketplace/microsoft"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-ink-100 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-ink-85 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-green-85">
              Browse Microsoft Marketplace
              <span aria-hidden="true" className="text-base leading-none">
                →
              </span>
            </Link>
          </div>
        </div>
      </section>
    )
  }

  const inputClass =
    'w-full rounded-md focus:rounded-md border border-primary-ink-10 px-3 py-2.5 text-sm focus:border-primary-green-85 focus:ring-0 focus:outline-none'

  return (
    <section className="min-w-0 bg-secondary-gray-10 py-10 lg:py-14">
      <div className="container min-w-0 max-w-6xl">
        <Link
          href="/marketplace/microsoft"
          className="inline-flex items-center gap-1.5 text-sm text-primary-ink-60 no-underline transition-colors hover:text-primary-ink-100">
          <svg
            className="h-4 w-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 12H5m6-6-6 6 6 6" />
          </svg>
          Continue shopping
        </Link>
        <h1 className="mt-4 text-[32px] font-medium leading-10 tracking-[-0.5px] text-primary-ink-100">Quote Cart</h1>
        <p className="mb-8 mt-1 text-base text-primary-ink-60">Review your selections and request a quote.</p>

        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_420px] lg:items-start">
          {/* The summary comes first on mobile, as in most checkouts, and sits beside the form from lg up */}
          <aside className="min-w-0 rounded-lg border border-primary-ink-10 bg-white lg:sticky lg:top-24 lg:order-2">
            <div className="flex items-baseline justify-between gap-3 border-b border-primary-ink-10 px-5 py-4">
              <h2 className="text-lg font-medium tracking-normal text-primary-ink-100">Quote summary</h2>
              <span className="text-sm text-primary-ink-60">
                {cart.items.length} {cart.items.length === 1 ? 'product' : 'products'}
              </span>
            </div>
            <table className="w-full text-sm">
              <caption className="sr-only">Line items in your quote cart with pricing and quantity</caption>
              <thead className="sr-only">
                <tr>
                  <th scope="col">Item</th>
                  <th scope="col">Monthly</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-primary-ink-10">
                {cart.items.map((item) => {
                  const lineKey = cartItemCompositeKey(item)
                  const segConfig = SEGMENT_CONFIG_MAP[item.segment]
                  const skuDisplay = cartItemSkuDisplay(item)
                  return (
                    <tr key={lineKey} className="align-top">
                      <td className="min-w-0 py-5 pl-5">
                        <div className="flex gap-4">
                          {/* The logo only names the vendor, so a phone spends its width on the line total instead */}
                          <span className="hidden sm:block">
                            <CartLineLogoTile vendor={item.vendor} />
                          </span>
                          <div className="min-w-0">
                            <p className="text-[13px] font-medium uppercase leading-5 tracking-[0.5px] text-primary-ink-60">
                              {segConfig?.shortLabel ?? item.segment}
                            </p>
                            <p className="font-medium leading-snug text-primary-ink-100">{item.productTitle}</p>
                            <p className="mt-0.5 text-xs text-primary-ink-60">
                              {/* Rails SKU codes run long with no break points, and would widen the table past the card */}
                              <span title={skuDisplay} className="[overflow-wrap:anywhere]">
                                {skuDisplay}
                              </span>{' '}
                              ·{' '}
                              {item.erpMonthly > 0
                                ? `${formatPrice(item.erpMonthly)}/mo each`
                                : formatPrice(item.erpMonthly)}
                            </p>
                            {/* Remove drops under the stepper when a wide line total leaves this column no room */}
                            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2">
                              <QuantityStepper
                                value={item.quantity}
                                onChange={(quantity) => cart.updateQuantity(lineKey, quantity)}
                                label={item.productTitle}
                              />
                              <RemoveLineButton
                                onClick={() => cart.removeItem(lineKey)}
                                label={`Remove ${item.productTitle} from cart`}
                              />
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-5 py-5 text-right tabular-nums">
                        <RollingNumber
                          value={`${formatPrice(item.erpMonthly * item.quantity)}${item.erpMonthly > 0 ? '/mo' : ''}`}
                          className="text-base font-medium text-primary-ink-100"
                        />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            <div className="border-t border-primary-ink-10 px-5 py-4">
              <div className="flex items-baseline justify-between gap-3">
                <p className="text-base font-medium text-primary-ink-100">
                  Estimated total{' '}
                  <span className="font-normal text-primary-ink-60">
                    ({totals.totalLicenses} {totals.totalLicenses === 1 ? 'license' : 'licenses'})
                  </span>
                </p>
                <p className="whitespace-nowrap text-xl font-medium tabular-nums text-primary-ink-100">
                  {formatPrice(totals.totalMonthly)}
                  {totals.totalMonthly > 0 && '/mo'}
                </p>
              </div>
              <div className="mt-1 flex justify-between text-sm tabular-nums text-primary-ink-60">
                <span>Billed annually</span>
                <span>
                  {formatPrice(totals.totalAnnual)}
                  {totals.totalAnnual > 0 && '/yr'}
                </span>
              </div>
            </div>
          </aside>

          {/* Request Quote form — inline, not modal */}
          <div className="min-w-0 rounded-lg border border-primary-ink-10 bg-white p-6 lg:order-1 lg:p-8">
            <h2 className="mb-5 text-2xl tracking-normal">Request Quote</h2>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="q-firstname" className="mb-1 block text-base text-primary-ink-85">
                    First Name <span className="text-red-600">*</span>
                  </label>
                  <input
                    id="q-firstname"
                    type="text"
                    placeholder="e.g. Jane"
                    {...register('firstname', { required: 'Required' })}
                    className={inputClass}
                  />
                  {errors.firstname && <p className="mt-0.5 text-xs text-red-600">{errors.firstname.message}</p>}
                </div>
                <div>
                  <label htmlFor="q-lastname" className="mb-1 block text-base text-primary-ink-85">
                    Last Name <span className="text-red-600">*</span>
                  </label>
                  <input
                    id="q-lastname"
                    type="text"
                    placeholder="e.g. Smith"
                    {...register('lastname', { required: 'Required' })}
                    className={inputClass}
                  />
                  {errors.lastname && <p className="mt-0.5 text-xs text-red-600">{errors.lastname.message}</p>}
                </div>
              </div>

              <div>
                <label htmlFor="q-email" className="mb-1 block text-base text-primary-ink-85">
                  Work Email <span className="text-red-600">*</span>
                </label>
                <input
                  id="q-email"
                  type="email"
                  placeholder="e.g. jane@company.com"
                  {...register('email', {
                    required: 'Required',
                    pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Invalid email' },
                    validate: validateEmailDomain,
                  })}
                  className={inputClass}
                />
                {errors.email && <p className="mt-0.5 text-xs text-red-600">{errors.email.message}</p>}
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="q-company" className="mb-1 block text-base text-primary-ink-85">
                    Company <span className="text-red-600">*</span>
                  </label>
                  <input
                    id="q-company"
                    type="text"
                    placeholder="e.g. Acme Corp"
                    {...register('company', { required: 'Required' })}
                    className={inputClass}
                  />
                  {errors.company && <p className="mt-0.5 text-xs text-red-600">{errors.company.message}</p>}
                </div>
                <div>
                  <label htmlFor="q-phone" className="mb-1 block text-base text-primary-ink-85">
                    Phone
                  </label>
                  <input
                    id="q-phone"
                    type="tel"
                    placeholder="e.g. (555) 123-4567"
                    {...register('phone')}
                    className={inputClass}
                  />
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="q-domain" className="mb-1 block text-base text-primary-ink-85">
                    Company Domain
                  </label>
                  <input
                    id="q-domain"
                    type="text"
                    placeholder="e.g. company.com"
                    {...register('company_domain')}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="q-tenant" className="mb-1 flex items-center gap-1 text-base text-primary-ink-85">
                    Microsoft Tenant ID
                    <span
                      className="group relative cursor-help rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-green-85"
                      tabIndex={0}
                      aria-describedby="q-tenant-tip">
                      <svg
                        aria-hidden="true"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        className="text-primary-ink-30">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M12 16v-4m0-4h.01" />
                      </svg>
                      <span
                        id="q-tenant-tip"
                        role="tooltip"
                        className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-1 w-56 -translate-x-1/2 rounded bg-primary-ink-100 px-2.5 py-1.5 text-[10px] leading-relaxed text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus:opacity-100">
                        Find at Azure Portal &gt; Azure AD &gt; Properties &gt; Tenant ID, or M365 Admin Center &gt;
                        Settings &gt; Org profile.
                      </span>
                    </span>
                  </label>
                  <input
                    id="q-tenant"
                    type="text"
                    {...register('microsoft_tenant_id', {
                      pattern: {
                        value: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
                        message: 'Enter a valid Tenant ID (UUID format)',
                      },
                    })}
                    className={inputClass}
                  />
                  {errors.microsoft_tenant_id && (
                    <p className="mt-0.5 text-xs text-red-600">{errors.microsoft_tenant_id.message}</p>
                  )}
                </div>
              </div>

              {hasGovSegment && (
                <div>
                  <label htmlFor="q-cmmc" className="mb-1 block text-base text-primary-ink-85">
                    CMMC Level
                  </label>
                  <select id="q-cmmc" {...register('cmmc_level')} className={`${inputClass} max-w-[280px]`}>
                    <option value="">Select if applicable</option>
                    <option value="level_1">Level 1 (Foundational)</option>
                    <option value="level_2">Level 2 (Advanced)</option>
                    <option value="level_3">Level 3 (Expert)</option>
                    <option value="not_sure">Not sure</option>
                  </select>
                </div>
              )}

              <div>
                <label htmlFor="q-notes" className="mb-1 block text-base text-primary-ink-85">
                  Notes
                </label>
                <textarea
                  id="q-notes"
                  rows={4}
                  placeholder="Any additional details about your licensing needs..."
                  {...register('message')}
                  className={inputClass}
                />
              </div>

              <fieldset>
                <legend className="mb-2 block text-base text-primary-ink-85">How would you like to follow up?</legend>
                <div className="space-y-3">
                  <SelectableCard
                    id="q-followup-call"
                    name="followup"
                    checked={wantsCall}
                    onSelect={() => setScheduleCallChoice(true)}
                    title="Meet with a solutions expert"
                    badge="Recommended"
                    description="Pick a time right after you submit."
                    icon={
                      // Castle UI calendar glyph (18x20) centered in a 24x24 frame
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path
                          transform="translate(3 2)"
                          d="M2 20C1.45 20 0.979167 19.8042 0.5875 19.4125C0.195833 19.0208 0 18.55 0 18V4C0 3.45 0.195833 2.97917 0.5875 2.5875C0.979167 2.19583 1.45 2 2 2H3V0.975C3 0.691667 3.09583 0.458333 3.2875 0.275C3.47917 0.0916667 3.71667 0 4 0C4.28333 0 4.52083 0.0958333 4.7125 0.2875C4.90417 0.479167 5 0.716667 5 1V2H13V0.975C13 0.691667 13.0958 0.458333 13.2875 0.275C13.4792 0.0916667 13.7167 0 14 0C14.2833 0 14.5208 0.0958333 14.7125 0.2875C14.9042 0.479167 15 0.716667 15 1V2H16C16.55 2 17.0208 2.19583 17.4125 2.5875C17.8042 2.97917 18 3.45 18 4V18C18 18.55 17.8042 19.0208 17.4125 19.4125C17.0208 19.8042 16.55 20 16 20H2ZM2 18H16V8H2V18ZM2 6H16V4H2V6Z"
                          fill="currentColor"
                        />
                      </svg>
                    }
                  />
                  <SelectableCard
                    id="q-followup-quote"
                    name="followup"
                    checked={!wantsCall}
                    onSelect={() => setScheduleCallChoice(false)}
                    title="Email me the quote"
                    description="We'll send your quote within 1 business day."
                    icon={
                      // Castle UI mail glyph
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M4 20C3.45 20 2.97917 19.8042 2.5875 19.4125C2.19583 19.0208 2 18.55 2 18V6C2 5.45 2.19583 4.97917 2.5875 4.5875C2.97917 4.19583 3.45 4 4 4H20C20.55 4 21.0208 4.19583 21.4125 4.5875C21.8042 4.97917 22 5.45 22 6V18C22 18.55 21.8042 19.0208 21.4125 19.4125C21.0208 19.8042 20.55 20 20 20H4ZM20 8L12.525 12.675C12.4417 12.725 12.3542 12.7625 12.2625 12.7875C12.1708 12.8125 12.0833 12.825 12 12.825C11.9167 12.825 11.8292 12.8125 11.7375 12.7875C11.6458 12.7625 11.5583 12.725 11.475 12.675L4 8V18H20V8ZM12 11L20 6H4L12 11ZM4 8.25V6.775V6.8V6.7875V8.25Z"
                          fill="currentColor"
                        />
                      </svg>
                    }
                  />
                </div>
              </fieldset>

              {submitError && (
                <div role="alert" className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">
                  {submitError}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full rounded-md bg-primary-ink-100 px-6 py-3 text-sm text-white transition-colors hover:bg-primary-ink-85 disabled:opacity-50">
                {isSubmitting ? (
                  'Submitting...'
                ) : (
                  // Keyed by choice so the label remounts and replays the fade on switch
                  <span key={wantsCall ? 'call' : 'quote'} className="block animate-slideUpAndFade">
                    {wantsCall ? 'Request Quote & Schedule Meeting' : 'Request Quote'}
                  </span>
                )}
              </button>

              <p className="flex items-center gap-1.5 text-sm text-[#0A7849]">
                <span>&#9679;</span> 1 business day turnaround
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  )
}
